/**
 * Media Item Cleanup Utilities
 * 
 * Handles proper cleanup of media resources when media items are removed,
 * particularly for video media items that may have associated video elements.
 */

import { MediaItem } from '../types';

/**
 * Clean up resources associated with a media item before removal
 * 
 * @param mediaItem - The media item being removed
 * @param videoRef - Optional video element ref if item is currently loaded
 */
export const cleanupMediaItemResources = async (
  mediaItem: MediaItem,
  videoRef?: React.RefObject<HTMLVideoElement>
): Promise<void> => {
  if (mediaItem.type !== 'video') {
    // Images don't need special cleanup
    console.log(`✅ Cleanup not needed for ${mediaItem.type} item: ${mediaItem.name}`);
    return;
  }

  console.log(`🧹 Cleaning up video media item: ${mediaItem.name}`);

  try {
    // If this video is currently loaded in the video player, we need to handle it
    if (videoRef?.current && videoRef.current.src.includes(mediaItem.url)) {
      console.log(`⚠️ Video is currently loaded, switching to empty source`);
      
      // Pause and clear the video
      videoRef.current.pause();
      videoRef.current.src = '';
      videoRef.current.load();
      
      // Small delay to ensure cleanup
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Revoke any object URLs if present (browser memory cleanup)
    if (mediaItem.url && mediaItem.url.startsWith('blob:')) {
      console.log(`🗑️ Revoking blob URL: ${mediaItem.url}`);
      URL.revokeObjectURL(mediaItem.url);
    }

    console.log(`✅ Media item cleanup complete: ${mediaItem.name}`);
  } catch (error) {
    console.error(`⚠️ Error during media item cleanup:`, error);
  }
};

/**
 * Clean up multiple media items
 * 
 * @param mediaItems - Array of media items to clean up
 * @param videoRef - Optional video element ref
 */
export const cleanupMultipleMediaItems = async (
  mediaItems: MediaItem[],
  videoRef?: React.RefObject<HTMLVideoElement>
): Promise<void> => {
  console.log(`🧹 Cleaning up ${mediaItems.length} media items...`);
  
  await Promise.all(
    mediaItems.map(item => cleanupMediaItemResources(item, videoRef))
  );
  
  console.log(`✅ Batch cleanup complete`);
};
